import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { IconBack, IconNext } from '../components/icons';
import { StockNav } from '../components/StockNav';
import { CallAheadBadge, Empty, ErrorBox, QueryView, StatusBadge } from '../components/ui';
import { addDays, formatDate, formatQty, isIsoDate, todayIso } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { useSite } from '../sites';
import { qk } from '../queryClient';

/** The day's confirmed orders for the stockers: totals against stock, each order's lines, the oldest lot to pull. */
export function PickPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [raw, setDate] = useSearchParam('date');
  const date = isIsoDate(raw) ? raw : todayIso();
  const { siteId } = useSite();
  const list = useQuery({ queryKey: qk.pickList(date, siteId ?? ''), queryFn: ({ signal }) => api.pickList(date, siteId, signal) });
  const picked = useMutation({
    mutationFn: (orderId: string) => api.setOrderStatus(orderId, 'picked'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', 'pick'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  return (
    <div className="stack">
      <section className="stack-sm">
        <h1>{t('pickTitle')}</h1>
        <StockNav />
        <p className="muted">{t('pickHint')}</p>
      </section>
      <div className="datebar">
        <button type="button" className="icon-btn" aria-label={t('prevDay')} onClick={() => setDate(addDays(date, -1))}>
          <IconBack />
        </button>
        <input type="date" aria-label={t('deliveryDate')} value={date} onChange={(e) => isIsoDate(e.target.value) && setDate(e.target.value)} />
        <button type="button" className="icon-btn" aria-label={t('nextDay')} onClick={() => setDate(addDays(date, 1))}>
          <IconNext />
        </button>
      </div>
      <p className="date-caption">
        <span>{formatDate(date, intlLocale, 'long')}</span>
      </p>
      {picked.error ? <ErrorBox error={picked.error} /> : null}
      <QueryView query={list}>
        {(pl) =>
          pl.orders.length === 0 ? (
            <Empty>{t('nothingToPick')}</Empty>
          ) : (
            <>
              <section className="card stack-sm">
                <h2>{t('totals')}</h2>
                <ul className="list">
                  {pl.totals.map((x) => (
                    <li key={x.itemId} className="row">
                      <span className="qty-pill">{formatQty(x.qty)}</span>
                      <span className="row-main">
                        <span className="row-title">{x.itemName}</span>
                        <span className="row-sub">
                          {t('onHand')} {formatQty(x.onHandPacks)}
                          {x.short > 0 ? (
                            <span className="badge badge-danger">
                              {formatQty(x.short)} {t('short')}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
              {pl.orders.map((o) => (
                <section key={o.orderId} className="card stack-sm">
                  <span className="row-title big">{o.customerName}</span>
                  <span className="badges">
                    <span className="badge">#{o.orderNo}</span>
                    <StatusBadge status={o.status} />
                    {o.callAhead ? <CallAheadBadge /> : null}
                  </span>
                  <ul className="list">
                    {o.lines.map((l) => (
                      <li key={l.orderLineId} className="row">
                        <span className="qty-pill">
                          {formatQty(l.qty)} {l.unit}
                        </span>
                        <span className="row-main">
                          <span className="row-title">{l.itemName}</span>
                          <span className="row-sub">
                            {l.lots.length > 0 ? (
                              <>
                                <span className="label">{t('lot')}</span> {l.lots[0]!.lotCode} · {l.lots[0]!.ageDays} {t('ageDays')} · {formatQty(l.lots[0]!.onHandPacks)}
                              </>
                            ) : (
                              t('noLots')
                            )}
                            {l.note ? ` · ${l.note}` : ''}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  {o.status === 'confirmed' ? (
                    <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => picked.mutate(o.orderId)} disabled={picked.isPending}>
                      ✓ {t('pickedBtn')}: #{o.orderNo}
                    </button>
                  ) : (
                    <span className="badge badge-ok">✓ {t('pickedAlready')}</span>
                  )}
                </section>
              ))}
            </>
          )
        }
      </QueryView>
    </div>
  );
}
